import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { gql, useApolloClient, useReactiveVar } from "@apollo/client";
import { useState, useEffect, useMemo, useRef } from "react";
import _ from "underscore";
import Button, { ButtonVariant } from "./Button";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import { primaryActionLoadingVar, startPrimaryAction, completePrimaryAction, saveToFutureFulfillmentsVar, getCurrentUserId, currentShopifyProductVariantsVar, currencySymbolVar, currentBusinessHasTooManyProductsVar, tooManyProductsThreshold } from "../helpers/client";
import { benefitFulfillmentText, formattedPrice, majorToMinorPrice } from '../helpers/tools';
import { v4 as uuid } from "uuid";
import SvgX from "../icons/SvgX";
import ExpandableArea from "./ExpandableArea";
import { useChooseSubscriptionScreenQuery, useGetProductVariantsQuery, DeliveryMethod } from "../types";
import { DateTime } from "luxon";
import AddressInput from "./AddressInput";
import ChooseProductSelection from "./ChooseProductSelection";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";
import VariantOption from "./VariantOption";
// import { withfriendsMixpanel as mixpanel } from "../helpers/mixpanel"
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { useSaveFulfillmentSettings, useUpdateUser, useUpdateVariantSelect } from "../cache/ChooseSubscriptionScreenFunctions";
import { customFilterSelectOption } from "@withfriendsco/wf-tools";
import DatePicker from "react-date-picker";
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import Select from 'react-windowed-select';
import { components } from 'react-select';
import { BiPlus } from 'react-icons/bi';
import { HiExternalLink } from 'react-icons/hi';
gql `
    fragment ChooseSubscriptionScreenAddress on Address {
      shippingName
      line1
      line2
      city
      state
      zip
      country
    }
  
    fragment ChooseSubscriptionScreenAddon on Addon {
      id
      description
      platformId
      platformName
      additionalCost {
        amount
      }
      applyDiscount
    }
  
    fragment ChooseSubscriptionScreenProduct on Product {
      id
      name
      platformId
      platformName
      platformVariantsOnlyMode
      curationValue
      variants {
        ...ChooseSubscriptionScreenVariant
      }
    }
  
    fragment UpdateVariantSelect on Fulfillment {
      variant {
        ...ChooseSubscriptionScreenVariant
      }
    }
  
    query ChooseSubscriptionScreen($id: ID!) {
      fulfillment(id: $id) {
        claimedAt
        id
        fulfillmentDate
        fulfillmentDateAdjustmentReason
        deliveryMethod
        variant {
          ...ChooseSubscriptionScreenVariant
        }
        productSelections {
          ...ChooseProductSelection
        }
        membership {
          id
          unfulfilledFulfillments: fulfillments(query: { isFulfilled: false}) {
            fulfillmentDate
            id
            benefit {
              id
            }
            address {
              ...ChooseSubscriptionScreenAddress
            }
            variant {
              ...ChooseSubscriptionScreenVariant
            }
            addons {
              ...ChooseSubscriptionScreenAddon
            }
            productSelections {
              ...ChooseProductSelection
            }
            shippingNotes
          }
          user {
            id
            firstName
            lastName
            phoneNumber
          }
          purchaser {
            id
            firstName
            lastName
          }
          tier {
            id
            name
            discounts {
              scope
              percentage
            }
          }
        }
        addons {
          ...ChooseSubscriptionScreenAddon
        }
        shippingNotes
        address {
          ...ChooseSubscriptionScreenAddress
        }
        isGift
        benefit {
          __typename
          id
          name
          ...on SubscriptionBenefit {
            isAddonOnly
            deliveredToYour
            shippingCost {
              amount
            }
            pickupEnabled
            shippingEnabled
            shippingIncluded
            shippingNotesInstructions
            shippingFrequency {
              period
            }
            deliveryFrequency
            delivery
            membershipTiers {
              members(status: Active){
                id
              }
              plans {
                kind
                price {
                  amount
                }
              }
            }
            business {
              id
              shopifyVersion
              enableProductSwapping
            }
            claimable
          }
          ... on DriveBenefit{
            deliveredToYour
            shippingNotesInstructions
            shippingCost {
              amount
            }
            pickupEnabled
            shippingEnabled
            claimable
          }
          ... on WelcomeGiftBenefit{
            deliveredToYour
            shippingNotesInstructions
            shippingCost {
              amount
            }
            pickupEnabled
            shippingEnabled
            claimable
          }
          products {
            ...ChooseSubscriptionScreenProduct
          }
          variants {
            ...ChooseSubscriptionScreenVariant
          }
          addons {
            id
            description
            additionalCost {
              amount
            }
            applyDiscount
            platformId
            platformName
          }
        }
        defaultCurations {
          productSelections {
            product {
              id
            }
            variant{ 
              id
            }
          }
          platformLineItems {
            quantity
            platformProductVariantId
          }
        }
        platformLineItems {
          quantity
          platformProductVariantId
          swapped
        }
        curated
      }
    }
  `;
const deliveryPeriod = (subscriptionBox) => {
    var _a;
    if (!!subscriptionBox.deliveryFrequency && !!((_a = subscriptionBox.shippingFrequency) === null || _a === void 0 ? void 0 : _a.period)) {
        let suffix = (subscriptionBox.deliveryFrequency > 1 ? subscriptionBox.deliveryFrequency + ' ' : '')
            + subscriptionBox.shippingFrequency.period.toLowerCase()
            + (subscriptionBox.deliveryFrequency > 1 ? 's' : '');
        // Handle special cases
        switch (suffix) {
            case "4 quarters":
            case "12 months":
            case "52 weeks":
                suffix = 'year';
                break;
            case "quarter":
            case "3 months":
            case "12 weeks":
            case "13 weeks":
                suffix = '3 months';
                break;
            case "4 weeks":
                suffix = 'month';
                break;
        }
        return suffix;
    }
    else {
        let suffixMap = {
            'annual': 'year',
            'quarterly': '3 months',
            'monthly': 'month',
            'biweekly': '2 weeks',
            'weekly': 'week'
        };
        return suffixMap[subscriptionBox.delivery.toLowerCase()];
    }
};
const ChooseSubscriptionScreen = ({ id, saveCallback, showCancelButton = false, fromFulfillmentScreen = false, isAfterPurchase = false, hasWelcomeGift = false }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79, _80, _81, _82;
    const namespace = '_' + uuid();
    const currentUserId = getCurrentUserId();
    const saveToFutureFulfillments = useReactiveVar(saveToFutureFulfillmentsVar);
    const updateVariantSelect = useUpdateVariantSelect(id);
    const [highlightedFields, setHighlightedFields] = useState([]);
    const [saveMessage, setSaveMessage] = useState(_jsx("span", {}));
    const [originalPlatformLineItems, setOriginalPlatformLineItems] = useState(null);
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    const client = useApolloClient();
    const { data, loading, error } = useChooseSubscriptionScreenQuery({
        variables: { id },
    });
    const fulfillmentDate = DateTime.fromSQL((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.fulfillmentDate);
    const [originalFulfillmentDate, setOriginalFulfillmentDate] = useState(null);
    const purchaserId = (_d = (_c = (_b = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _b === void 0 ? void 0 : _b.membership) === null || _c === void 0 ? void 0 : _c.purchaser) === null || _d === void 0 ? void 0 : _d.id.toString();
    const memberId = (_g = (_f = (_e = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _e === void 0 ? void 0 : _e.membership) === null || _f === void 0 ? void 0 : _f.user) === null || _g === void 0 ? void 0 : _g.id.toString();
    const currentShopifyVersion = ((_j = (_h = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _h === void 0 ? void 0 : _h.benefit) === null || _j === void 0 ? void 0 : _j.__typename) == "SubscriptionBenefit" && ((_m = (_l = (_k = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _k === void 0 ? void 0 : _k.benefit) === null || _l === void 0 ? void 0 : _l.business) === null || _m === void 0 ? void 0 : _m.shopifyVersion);
    const currentBusinessHasTooManyProducts = currentBusinessHasTooManyProductsVar();
    const currentBusinessId = ((_p = (_o = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _o === void 0 ? void 0 : _o.benefit) === null || _p === void 0 ? void 0 : _p.__typename) == "SubscriptionBenefit" ? (_s = (_r = (_q = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _q === void 0 ? void 0 : _q.benefit) === null || _r === void 0 ? void 0 : _r.business) === null || _s === void 0 ? void 0 : _s.id : null;
    const firstCurationLoadHasOccurred = useRef(false);
    const resetCuration = () => {
        if (!defaultCuration || !selectRef.current)
            return;
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                platformLineItems: () => {
                    return defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems;
                },
            },
        });
        selectRef.current.setValue(defaultCurationOptions);
    };
    const hasSelectionChanged = () => {
        var _a;
        if (originalPlatformLineItems === null)
            return false;
        const currentPlatformLineItems = ((_a = client.readFragment({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fragment: gql `
          fragment CurrentPlatformLineItems on Fulfillment {
            platformLineItems {
              quantity
              platformProductVariantId
            }
          }
        `
        })) === null || _a === void 0 ? void 0 : _a.platformLineItems) || [];
        return !_.isEqual(currentPlatformLineItems.map(({ quantity, platformProductVariantId }) => ({ quantity, platformProductVariantId })), originalPlatformLineItems.map(({ quantity, platformProductVariantId }) => ({ quantity, platformProductVariantId })));
    };
    const { data: dataProducts, error: errorProducts, stopPolling: stopPollingProducts } = useGetProductVariantsQuery({
        variables: {
            id: currentBusinessId,
        },
        pollInterval: 1000,
        skip: !currentBusinessId || currentShopifyVersion != 2 || currentBusinessHasTooManyProducts
    });
    useEffect(() => {
        var _a;
        if (((_a = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _a === void 0 ? void 0 : _a.shopifyProductsCount) > tooManyProductsThreshold) {
            currentBusinessHasTooManyProductsVar(true);
            stopPollingProducts();
        }
    }, [(_t = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _t === void 0 ? void 0 : _t.shopifyProductsCount]);
    useEffect(() => {
        var _a;
        if (((_a = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _a === void 0 ? void 0 : _a.shopifyProductVariants) != null) {
            stopPollingProducts();
            currentShopifyProductVariantsVar(dataProducts.business.shopifyProductVariants);
        }
    }, [(_u = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _u === void 0 ? void 0 : _u.shopifyProductVariants]);
    useEffect(() => {
        var _a;
        if (((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) && originalPlatformLineItems === null) {
            setOriginalPlatformLineItems(data.fulfillment.platformLineItems);
        }
    }, [(_v = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _v === void 0 ? void 0 : _v.platformLineItems, originalPlatformLineItems]);
    // GPT 
    function useDeepMemo(value) {
        const ref = useRef();
        if (!_.isEqual(value, ref.current)) {
            ref.current = value;
        }
        return ref.current;
    }
    const memoizedProductSelections = useDeepMemo((_w = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _w === void 0 ? void 0 : _w.productSelections);
    useEffect(() => {
        var _a;
        if (!!((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections)) {
            if (firstCurationLoadHasOccurred.current)
                resetCuration();
            else
                firstCurationLoadHasOccurred.current = true;
        }
    }, [memoizedProductSelections]);
    const Input = (props) => (_jsxs("div", { style: { borderBottom: '1px solid #ccc', position: 'relative', width: "100%" }, children: [_jsx(components.Input, Object.assign({}, props)), _jsx("span", { style: { position: 'absolute', right: ".4em", top: "calc(50% - 0.5em)" }, children: _jsx(BiPlus, {}) })] }));
    const DropdownIndicator = () => null;
    const Placeholder = () => null;
    const selectRef = useRef(null);
    const currentShopifyProductVariants = useReactiveVar(currentShopifyProductVariantsVar);
    const currencySymbol = currencySymbolVar();
    // Get default platform line items by searching default Curations
    const defaultCuration = (_y = (_x = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _x === void 0 ? void 0 : _x.defaultCurations) === null || _y === void 0 ? void 0 : _y.find((defaultCuration) => {
        var _a;
        const a = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections.map((productSelection) => {
            var _a;
            return {
                product: productSelection.product.id,
                variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
            };
        }).sort((a, b) => a.product.localeCompare(b.product));
        const b = defaultCuration.productSelections.map((productSelection) => {
            var _a;
            return {
                product: productSelection.product.id,
                variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
            };
        }).sort((a, b) => a.product.localeCompare(b.product));
        return _.isEqual(a, b);
    });
    const defaultCurationPlatformLineItems = (_0 = (_z = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _z === void 0 ? void 0 : _z.defaultCurations) === null || _0 === void 0 ? void 0 : _0.flatMap((defaultCuration) => {
        return defaultCuration.platformLineItems || [];
    });
    let aggregateCurationValue = (_3 = (_2 = (_1 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _1 === void 0 ? void 0 : _1.benefit) === null || _2 === void 0 ? void 0 : _2.products) === null || _3 === void 0 ? void 0 : _3.reduce((acc, product) => {
        acc += product.curationValue;
        return acc;
    }, 0);
    const discounts = (_6 = (_5 = (_4 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _4 === void 0 ? void 0 : _4.membership) === null || _5 === void 0 ? void 0 : _5.tier) === null || _6 === void 0 ? void 0 : _6.discounts;
    let retailDiscount = { scope: "RETAIL", percentage: 0 };
    if (discounts) {
        const newDiscount = discounts.find((d) => d.scope === "RETAIL");
        if (newDiscount) {
            retailDiscount = newDiscount;
        }
    }
    const discountAddon = (cost) => {
        return { amount: cost.amount * (1.0 - 0.01 * retailDiscount.percentage) };
    };
    const undiscountAddon = (cost) => {
        return { amount: cost.amount / (1.0 - 0.01 * retailDiscount.percentage) };
    };
    const discountedAggregateCurationValue = discountAddon({ amount: aggregateCurationValue }).amount;
    let defaultCurationValue = ((_7 = defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) === null || _7 === void 0 ? void 0 : _7.reduce((acc, platformLineItem) => {
        const matchedOption = currentShopifyProductVariants === null || currentShopifyProductVariants === void 0 ? void 0 : currentShopifyProductVariants.find(productVariant => productVariant.id.split("/").pop() == platformLineItem.platformProductVariantId);
        acc += parseFloat(matchedOption === null || matchedOption === void 0 ? void 0 : matchedOption.price) || 0;
        return acc;
    }, 0)) || 0;
    let discountedDefaultCurationValue = discountAddon({ amount: defaultCurationValue }).amount;
    discountedDefaultCurationValue = discountedDefaultCurationValue > discountedAggregateCurationValue ? discountedDefaultCurationValue : discountedAggregateCurationValue;
    const price = (_9 = (_8 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _8 === void 0 ? void 0 : _8.platformLineItems) === null || _9 === void 0 ? void 0 : _9.reduce((acc, platformLineItem) => {
        const matchedOption = currentShopifyProductVariants === null || currentShopifyProductVariants === void 0 ? void 0 : currentShopifyProductVariants.find(productVariant => productVariant.id.split("/").pop() == platformLineItem.platformProductVariantId);
        acc += parseFloat(matchedOption === null || matchedOption === void 0 ? void 0 : matchedOption.price) || 0;
        return acc;
    }, 0);
    const discountedPrice = discountAddon({ amount: price }).amount;
    const discountedAddonAmount = (discountedPrice - discountedDefaultCurationValue) > 0 ? (discountedPrice - discountedDefaultCurationValue) : 0;
    const options = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        // TODO - I'm coding this live so i don't have time to do this right, but the right way to do this is to set this as an unrelated effect and have it happen first. 
        const stupidDefaultCuration = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.defaultCurations) === null || _b === void 0 ? void 0 : _b.find((defaultCuration) => {
            var _a;
            const a = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections.map((productSelection) => {
                var _a;
                return {
                    product: productSelection.product.id,
                    variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
                };
            }).sort((a, b) => a.product.localeCompare(b.product));
            const b = defaultCuration.productSelections.map((productSelection) => {
                var _a;
                return {
                    product: productSelection.product.id,
                    variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
                };
            }).sort((a, b) => a.product.localeCompare(b.product));
            return _.isEqual(a, b);
        });
        //@ts-ignore
        let minimumInventory = (_e = (_d = (_c = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _c === void 0 ? void 0 : _c.benefit) === null || _d === void 0 ? void 0 : _d.membershipTiers) === null || _e === void 0 ? void 0 : _e.reduce((count, tier) => { var _a; return count + ((_a = tier.members) === null || _a === void 0 ? void 0 : _a.length); }, 0);
        minimumInventory = 4; // below, we pass all products already in the selection, so this is just for customizations 
        let swapCollection = null;
        switch ((_g = (_f = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _f === void 0 ? void 0 : _f.benefit) === null || _g === void 0 ? void 0 : _g.id) {
            // @ts-ignore 
            case "15184329": // Psychic damage: BLK & GRN
                minimumInventory = 8;
            case "15183415": // BLK + GRN all tiers
            case "15185990":
                swapCollection = "Shop In Stock";
                break;
            case "20385356": // Psychic damage: Silver Sprocket
                swapCollection = "Downloads";
                break;
        }
        return currentShopifyProductVariants === null || currentShopifyProductVariants === void 0 ? void 0 : currentShopifyProductVariants.filter(pv => {
            var _a, _b, _c;
            const platformProductVariantId = pv.id.split("/").pop();
            const isAlreadyInSelection = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) === null || _b === void 0 ? void 0 : _b.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            const isInDefaultCuration = defaultCurationPlatformLineItems === null || defaultCurationPlatformLineItems === void 0 ? void 0 : defaultCurationPlatformLineItems.some(item => item.platformProductVariantId === platformProductVariantId);
            const doesNotTrackInventory = !pv.inventoryTracked;
            const hasMinimumInventory = pv.inventory >= minimumInventory;
            const inSwapCollection = !swapCollection || ((_c = pv.collections) === null || _c === void 0 ? void 0 : _c.some(collection => collection == swapCollection));
            const hasPrice = !!parseFloat(pv.price);
            const hasWebLink = !pv.onlineStoreUrl.includes('shopifypreview');
            return isInDefaultCuration || isAlreadyInSelection || (hasWebLink && hasPrice && (hasMinimumInventory || doesNotTrackInventory) && inSwapCollection);
        }).map(productVariant => {
            var _a;
            // const isAlreadyInSelection = data?.fulfillment?.platformLineItems?.find((platformLineItem) => platformLineItem.platformProductVariantId == productVariant.id.split("/").pop())
            const platformProductVariantId = productVariant.id.split("/").pop();
            const isInDefaultCuration = (_a = stupidDefaultCuration === null || stupidDefaultCuration === void 0 ? void 0 : stupidDefaultCuration.platformLineItems) === null || _a === void 0 ? void 0 : _a.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            const productVariantPrice = parseFloat(productVariant.price);
            const discountedProductVariantPrice = discountAddon({ amount: productVariantPrice }).amount;
            let discountedAddonPriceTotal = 0;
            let discountedAddonPriceForProductVariant = 0;
            let addonPriceTotal = 0;
            let addonPriceForProductVariant = 0;
            const possibleDiscountedAddonTotal = discountedPrice + discountedProductVariantPrice - discountedDefaultCurationValue;
            if (possibleDiscountedAddonTotal > 0) {
                discountedAddonPriceTotal = possibleDiscountedAddonTotal;
                discountedAddonPriceForProductVariant = (discountedAddonPriceTotal < discountedProductVariantPrice) ? discountedAddonPriceTotal : discountedProductVariantPrice;
                addonPriceTotal = undiscountAddon({ amount: discountedAddonPriceTotal }).amount;
                addonPriceForProductVariant = undiscountAddon({ amount: discountedAddonPriceForProductVariant }).amount;
            }
            return {
                displayName: productVariant.displayName,
                vendor: productVariant.vendor,
                productTitle: productVariant.productTitle,
                variantTitle: productVariant.variantTitle,
                description: productVariant.description,
                value: productVariant.id.split("/").pop() + "," + productVariant.displayName,
                label: (_jsxs("div", { className: "wf-option-wrapper" + (isInDefaultCuration ? " wf-in-default-curation" : ""), style: { display: 'flex', alignItems: 'center' }, children: [_jsx("img", { src: productVariant.imageUrl, alt: productVariant.displayName, style: {
                                width: '100px',
                                marginRight: '20px',
                                border: '1px solid #ddd',
                                borderRadius: '0',
                                visibility: !!productVariant.imageUrl ? 'visible' : 'hidden'
                            } }), _jsxs("div", { children: [_jsxs("div", { className: "flex items-center", children: [_jsx("strong", { style: { display: 'block', marginBottom: '5px' }, children: productVariant.displayName }), productVariant.onlineStoreUrl && (_jsx(_Fragment, { children: _jsx("div", { className: "wf-product-link ml-3 mb-2", role: "button", onPointerDown: (e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    window.open(productVariant.onlineStoreUrl, "_blank");
                                                }, children: _jsx(HiExternalLink, { className: "text-wfGray-600 hover:text-salmon-600" }) }) }))] }), productVariant.vendor && _jsxs("span", { style: { display: 'block', marginBottom: '5px' }, children: ["by ", productVariant.vendor] }), !!discountedAddonPriceForProductVariant ? (_jsxs("p", { className: "mt-2 text-sm font-medium wf-product-variant-addon-price hidden", children: [retailDiscount.percentage > 0 && (_jsxs("span", { className: "line-through text-gray-500 mr-2", children: ["+", formattedPrice(majorToMinorPrice(addonPriceForProductVariant))] })), _jsxs("span", { className: "text-wfRed-600", children: ["+", formattedPrice(majorToMinorPrice(discountedAddonPriceForProductVariant))] })] })) : (_jsx("p", { className: "mt-2 text-sm font-medium wf-product-variant-addon-price hidden", children: _jsx("span", { className: "text-gray-500 mr-2", children: "No additional charge" }) }))] })] }))
            };
        });
    }, [(_10 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _10 === void 0 ? void 0 : _10.platformLineItems, currentShopifyProductVariants, (_11 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _11 === void 0 ? void 0 : _11.productSelections]);
    const isAddonOnly = ((_13 = (_12 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _12 === void 0 ? void 0 : _12.benefit) === null || _13 === void 0 ? void 0 : _13.id) && ["22624156", "22945597"].includes((_15 = (_14 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _14 === void 0 ? void 0 : _14.benefit) === null || _15 === void 0 ? void 0 : _15.id); // Psychic Damage: Casita Bookstore
    useEffect(() => {
        if (isAddonOnly) {
            document.querySelectorAll('.wf-option-wrapper.wf-in-default-curation').forEach(x => { var _a; return (_a = x.parentElement.parentElement.querySelector('[aria-label^="Remove"]')) === null || _a === void 0 ? void 0 : _a.classList.add('hidden'); });
            document.querySelectorAll('.wf-option-wrapper:not(.wf-in-default-curation)').forEach(x => { var _a; return (_a = x.parentElement.parentElement.querySelector('[aria-label^="Remove"]')) === null || _a === void 0 ? void 0 : _a.classList.remove('hidden'); });
        }
    }, [(_16 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _16 === void 0 ? void 0 : _16.platformLineItems, currentShopifyProductVariants, isAddonOnly]);
    const defaultCurationOptions = (_17 = defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) === null || _17 === void 0 ? void 0 : _17.map((platformLineItem) => options === null || options === void 0 ? void 0 : options.find(option => option.value.split(",")[0].split("/").pop() == platformLineItem.platformProductVariantId)).filter(Boolean);
    const defaultOptions = (_19 = (_18 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _18 === void 0 ? void 0 : _18.platformLineItems) === null || _19 === void 0 ? void 0 : _19.map((platformLineItem) => options === null || options === void 0 ? void 0 : options.find(option => option.value.split(",")[0].split("/").pop() == platformLineItem.platformProductVariantId)).filter(Boolean);
    useEffect(() => {
        // mixpanel.track("Viewed Choose Subscription Screen")
    }, []);
    useEffect(() => {
        // @ts-ignore
        for (const el of document.getElementsByClassName("wf-button-back")) {
            if (el instanceof HTMLElement) {
                el.style.display = "none";
            }
        }
    }, []);
    useEffect(() => {
        if (!originalFulfillmentDate && fulfillmentDate.isValid)
            setOriginalFulfillmentDate(fulfillmentDate);
    }, [fulfillmentDate]);
    const { wrapped: updateUser } = useUpdateUser(memberId);
    const { wrapped: saveSettings } = useSaveFulfillmentSettings({ id, includeDate: fromFulfillmentScreen });
    const closeSaveMessage = () => {
        setSaveMessage(_jsx("span", {}));
    };
    const checkRequiredFields = () => {
        var _a, _b;
        const requiredFields = (userIsMember || userIsOrganizer) && data.fulfillment.deliveryMethod == DeliveryMethod.Shipping ? [
            "line1",
            "city",
            "state",
            "zip",
            "country",
        ] : [];
        let filteredFields = requiredFields.filter((k) => {
            const v = data.fulfillment.address[k];
            return !v || v === "";
        });
        if (shippingNotesRequired && !data.fulfillment.shippingNotes)
            filteredFields.push("shippingNotes");
        if ((_a = data.fulfillment.benefit.products) === null || _a === void 0 ? void 0 : _a.length) {
            //@ts-ignore
            let needsVariant = data.fulfillment.benefit.products.reduce((needsVariant, product) => {
                var _a, _b, _c;
                return (needsVariant ||
                    !!((_a = product.variants) === null || _a === void 0 ? void 0 : _a.length) && !((_c = (_b = data.fulfillment.productSelections) === null || _b === void 0 ? void 0 : _b.find((productSelection) => { var _a; return ((_a = productSelection.product) === null || _a === void 0 ? void 0 : _a.id) == product.id; })) === null || _c === void 0 ? void 0 : _c.variant));
            }, false);
            if (needsVariant)
                filteredFields = ["option", ...filteredFields];
        }
        if ((_b = data.fulfillment.benefit.variants) === null || _b === void 0 ? void 0 : _b.length)
            if (!data.fulfillment.variant)
                filteredFields = ["option", ...filteredFields];
        if (fulfillmentDateAdjusted)
            if (!data.fulfillment.fulfillmentDateAdjustmentReason)
                filteredFields = ["reason for changing shipment date", ...filteredFields];
        return Array.from(new Set(filteredFields));
    };
    const cancelSaveFulfillment = async () => {
        // mixpanel.track("Canceled Save Fulfillment")
        if (saveCallback) {
            saveCallback();
        }
    };
    const saveFulfillment = async () => {
        var _a, _b;
        //@ts-ignore
        const blankRequiredFields = ((_b = (_a = data.fulfillment) === null || _a === void 0 ? void 0 : _a.benefit) === null || _b === void 0 ? void 0 : _b.deliveredToYour) == "door" ? checkRequiredFields() : [];
        if (blankRequiredFields.length) {
            setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Error! The following required fields were blank:", _jsx("ul", { children: blankRequiredFields.map((f) => {
                            return _jsx("li", { className: "text-salmon-600", children: f === 'shippingNotes' ? shippingNotesLabel : f });
                        }) })] }));
            setHighlightedFields(blankRequiredFields);
            return;
        }
        startPrimaryAction();
        // mixpanel.track("Saved Fulfillment")
        await updateUser();
        const response = await saveSettings({
            refetchQueries: ["ChooseSubscriptionScreen", "ManageFulfillments"],
            awaitRefetchQueries: true,
        });
        if (response.data) {
            setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Benefit saved! Hit close to continue."] }));
            if (saveCallback) {
                saveCallback();
            }
            if (!fromFulfillmentScreen) {
                // @ts-ignore
                for (const el of document.getElementsByClassName("wf-button-back")) {
                    if (el instanceof HTMLElement) {
                        el.click();
                    }
                }
            }
        }
        else {
            setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Error! Please reload and try again, or contact support@withfriends.co."] }));
        }
        completePrimaryAction();
    };
    const updateStateSelect = (state) => {
        client.writeFragment({
            id: `Fulfillment:${id}`,
            fragment: gql `
          fragment FulfillmentAddressState on Fulfillment {
            address {
              state
            }
          }
        `,
            data: {
                address: {
                    state
                }
            },
        });
    };
    const updateCountrySelect = (country) => {
        client.writeFragment({
            id: `Fulfillment:${id}`,
            fragment: gql `
          fragment FulfillmentAddressCountry on Fulfillment {
            address {
              country
            }
          }
        `,
            data: {
                address: {
                    country
                }
            },
        });
    };
    const addonCheckboxValue = (id) => {
        return ((data === null || data === void 0 ? void 0 : data.fulfillment.addons) || []).some((a) => a.id === id);
    };
    const updatePhoneNumber = (ev) => {
        var _a, _b, _c;
        client.cache.modify({
            id: `User:${(_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.membership) === null || _b === void 0 ? void 0 : _b.user) === null || _c === void 0 ? void 0 : _c.id}`,
            fields: {
                phoneNumber() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateAddonCheckbox = (id) => {
        return (ev) => {
            // mixpanel.track("Clicked Addon Checkbox")
            if (ev.target.checked) {
                client.cache.modify({
                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                    fields: {
                        addons(existingAddonsRefs = []) {
                            const newAddonRef = {
                                __ref: `Addon:${id}`,
                            };
                            return [...existingAddonsRefs, newAddonRef];
                        },
                    },
                });
            }
            else {
                client.cache.modify({
                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                    fields: {
                        addons(existingAddonsRefs = []) {
                            const filtered = existingAddonsRefs.filter((a) => a.__ref !== `Addon:${id}`);
                            return [...filtered];
                        },
                    },
                });
            }
        };
    };
    if (loading) {
        return _jsx(LoadingScreen, {});
    }
    if (currentShopifyProductVariants === null && currentShopifyVersion == 2 && !currentBusinessHasTooManyProducts) {
        return _jsx(LoadingScreen, {});
    }
    if (error) {
        return _jsx(ErrorScreen, { message: error.message });
    }
    if (!data.fulfillment) {
        return _jsx(ErrorScreen, { message: "404: Fulfillment Not Found" });
    }
    const addonCost = (((_20 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _20 === void 0 ? void 0 : _20.addons) || []).reduce((accum, addon) => {
        return { amount: accum.amount + addon.additionalCost.amount };
    }, { amount: 0 });
    const addonDiscountedCost = (((_21 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _21 === void 0 ? void 0 : _21.addons) || []).reduce((accum, addon) => {
        if (addon.applyDiscount) {
            accum.amount = accum.amount + discountAddon(addon.additionalCost).amount;
        }
        else {
            accum.amount = accum.amount + addon.additionalCost.amount;
        }
        return accum;
    }, { amount: 0 });
    const formatTotal = () => {
        if (addonCost.amount > addonDiscountedCost.amount) {
            return (_jsxs("span", { children: ["\u00A0", _jsx("span", { className: "line-through", style: { textDecoration: "line-through" }, children: formatAmount({ amount: addonCost.amount }) }), _jsxs("span", { className: "font-heavy", children: [" ", formatAmount({ amount: addonDiscountedCost.amount })] })] }));
        }
        else {
            return _jsx("span", { className: "font-heavy", children: formatAmount({ amount: addonCost.amount }) });
        }
    };
    const formatAmount = (a, applyDiscount = false) => {
        const base = `(+ $${(a.amount * 0.01).toFixed(2)})`;
        if (retailDiscount.percentage && applyDiscount) {
            const newBase = `(+ $${(discountAddon(a).amount * 0.01).toFixed(2)})`;
            return (_jsxs("span", { children: ["\u00A0", _jsx("span", { className: "line-through", style: { textDecoration: "line-through" }, children: base }), _jsxs("span", { className: "font-heavy", children: [" ", newBase] })] }));
        }
        else {
            return _jsxs("span", { children: [" ", base] });
        }
    };
    const updateShippingNotes = (ev) => {
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                shippingNotes() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateFulfillmentDateAdjustmentReason = (ev) => {
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                fulfillmentDateAdjustmentReason() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateFulfillmentWithSelectValue = (value) => {
        const fieldValue = value.map(eachValue => {
            var _a;
            const platformProductVariantId = eachValue.value.split(",")[0].split("/").pop();
            const isInDefaultCuration = (_a = defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) === null || _a === void 0 ? void 0 : _a.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            return {
                quantity: 1,
                platformProductVariantId: platformProductVariantId,
                swapped: !isInDefaultCuration
            };
        });
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                platformLineItems: () => {
                    return fieldValue;
                },
            },
        });
    };
    const userIsPurchaser = !!(purchaserId &&
        currentUserId &&
        purchaserId == currentUserId);
    const userIsMember = !!(memberId &&
        currentUserId &&
        memberId == currentUserId);
    const userIsOrganizer = fromFulfillmentScreen;
    const fulfillmentDateAdjusted = !!originalFulfillmentDate && !originalFulfillmentDate.equals(fulfillmentDate);
    if (errorProducts)
        return _jsx(ErrorScreen, { message: errorProducts.message });
    let shippingNotesLabel = "Shipping Notes";
    let shippingNotesRequired = false;
    let shippingNotesAllowedCharacters = null;
    switch ((_23 = (_22 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _22 === void 0 ? void 0 : _22.benefit) === null || _23 === void 0 ? void 0 : _23.id) {
        case "20508976":
        case "20508979":
        case "20508982":
            shippingNotesLabel = "Kit ID"; // Psychic damage: Dante Labs
            shippingNotesRequired = true;
            shippingNotesAllowedCharacters = "A-Z0-9";
            break;
        default:
            break;
    }
    return (_jsxs("div", { className: "font-sans w-full max-w-xl wf-choose-subscription-screen-wrapper", children: [_jsx("style", { dangerouslySetInnerHTML: { __html: `
          [id^="react-select-"][id$="-listbox"] p.wf-product-variant-addon-price {
            display: initial !important;
          }
          .wf-choose-subscription-screen-wrapper div.[role="button"][aria-label^="Remove"].hidden {
            display: none !important;
          }
        ` } }), data.fulfillment.benefit.addons.length > 0 && data.fulfillment.benefit.__typename == "SubscriptionBenefit" && data.fulfillment.benefit.isAddonOnly ? (_jsxs(_Fragment, { children: [_jsx("h3", { className: "mt-4 mb-4 block pt-4", children: _jsxs("strong", { children: [data.fulfillment.benefit.name, " Add-On"] }) }), _jsxs("p", { className: "mb-4 block text-m", children: ["Want even more from your membership? You can add our ", _jsx("strong", { children: data.fulfillment.benefit.name }), " for an additional charge each ", deliveryPeriod(data.fulfillment.benefit), "."] })] })) : (data.fulfillment.benefit.__typename == "WelcomeGiftBenefit" && isAfterPurchase ? (_jsxs("p", { className: "mb-4 block text-m", children: ["As a new member, you'll receive a ", _jsx("strong", { children: data.fulfillment.benefit.name }), " \u2013 a little welcome from us to you!"] })) : (_jsx("h3", { className: "my-4 block", children: _jsx("strong", { children: data.fulfillment.benefit.name }) }))), !hasWelcomeGift && (_jsxs(_Fragment, { children: [((_24 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _24 === void 0 ? void 0 : _24.isGift) &&
                        ((_27 = (_26 = (_25 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _25 === void 0 ? void 0 : _25.membership) === null || _26 === void 0 ? void 0 : _26.purchaser) === null || _27 === void 0 ? void 0 : _27.id) && (_jsxs("div", { className: "my-4 block", children: ["Purchaser: ", (_29 = (_28 = data.fulfillment.membership) === null || _28 === void 0 ? void 0 : _28.purchaser) === null || _29 === void 0 ? void 0 : _29.firstName, " ", (_31 = (_30 = data.fulfillment.membership) === null || _30 === void 0 ? void 0 : _30.purchaser) === null || _31 === void 0 ? void 0 : _31.lastName] })), _jsxs("div", { className: "my-4 block", children: ["Member: ", (_32 = data.fulfillment.membership) === null || _32 === void 0 ? void 0 : _32.user.firstName, " ", (_33 = data.fulfillment.membership) === null || _33 === void 0 ? void 0 : _33.user.lastName] })] })), _jsx("div", { className: "block text-wfGray-800", children: userIsOrganizer ? (_jsxs("div", { className: "my-4 block w-full", children: [_jsx("label", { className: "my-2 block", children: benefitFulfillmentText(data.fulfillment.benefit)('fulfillment_date') }), _jsx(DatePicker, { className: "block w-full", returnValue: "start", onChange: (value) => {
                                // This forces the type to be a Date
                                let date;
                                if (value instanceof Date) {
                                    date = value;
                                }
                                else {
                                    return;
                                }
                                client.cache.modify({
                                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                                    fields: {
                                        fulfillmentDate() {
                                            return DateTime.fromISO(date.toISOString()).toSQLDate();
                                        },
                                    },
                                });
                            }, value: fulfillmentDate.toJSDate() }), fulfillmentDateAdjusted ? (_jsxs("div", { children: [_jsx("label", { className: "my-4 block", htmlFor: "phoneNumber", children: "Reason for changing fulfillment date (member does not see this)" }), _jsx(TextInput, { id: "fulfillmentDateAdjustmentReason", required: true, value: (_34 = data.fulfillment) === null || _34 === void 0 ? void 0 : _34.fulfillmentDateAdjustmentReason, onChange: updateFulfillmentDateAdjustmentReason, className: "p-2 w-full text-lg", placeholder: "e.g. Member asked for special delivery date", highlighted: _.contains(highlightedFields, "reason for changing shipment date") })] })) : null] })) : (_jsxs(_Fragment, { children: [benefitFulfillmentText(data.fulfillment.benefit)('fulfillment_date'), ": ", fulfillmentDate.toLocaleString()] })) }), !!((_37 = (_36 = (_35 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _35 === void 0 ? void 0 : _35.benefit) === null || _36 === void 0 ? void 0 : _36.addons) === null || _37 === void 0 ? void 0 : _37.length) && (_jsxs(_Fragment, { children: [_jsx("h6", { className: "mb-2 block pt-4", children: _jsx("strong", { children: data.fulfillment.benefit.__typename == "SubscriptionBenefit" && data.fulfillment.benefit.isAddonOnly ? "Add this to your membership:" : "Add something extra:" }) }), _jsx("div", { className: "mb-4 block text-wfGray-600 text-sm", children: "(Select all that apply. Charges will apply to each fulfillment.)" })] })), (!!((_38 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _38 === void 0 ? void 0 : _38.isGift) && !userIsPurchaser && !userIsOrganizer && ((_41 = (_40 = (_39 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _39 === void 0 ? void 0 : _39.benefit) === null || _40 === void 0 ? void 0 : _40.addons) === null || _41 === void 0 ? void 0 : _41.length)) ? (_jsxs("div", { className: "p-4 my-4 border-2 border-salmon-600 prose", children: [_jsx("strong", { className: "font-heavy", children: "Note: Only the gift-giver can edit addons for an existing gift membership!" }), " ", "If you're a gift recipient and you absolutely need to update addons, please get in touch with us at", " ", _jsx("a", { href: "mailto:team@withfriends.co", children: "team@withfriends.co" }), ". Thank you!"] })) : "", (((_43 = (_42 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _42 === void 0 ? void 0 : _42.benefit) === null || _43 === void 0 ? void 0 : _43.addons) || []).map((a) => {
                var _a;
                return (_jsxs("div", { className: "my-2 text-md grid grid-cols-12 gap-2 items-center", children: [_jsx("div", { className: "col-span-1 flex items-center", children: _jsx("input", { className: "inline", type: "checkbox", disabled: !!((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.isGift) && !userIsPurchaser && !userIsOrganizer, name: `addon-${a.id}`, id: `addon-${a.id}`, value: a.id, checked: addonCheckboxValue(a.id), onChange: updateAddonCheckbox(a.id) }) }), _jsxs("label", { className: "font-sans normal-case col-span-11 flex items-center", htmlFor: `addon-${a.id}`, children: [a.description, a.additionalCost.amount > 0
                                    ? formatAmount({ amount: a.additionalCost.amount }, a.applyDiscount)
                                    : null] })] }, a.id));
            }), !!((_46 = (_45 = (_44 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _44 === void 0 ? void 0 : _44.benefit) === null || _45 === void 0 ? void 0 : _45.addons) === null || _46 === void 0 ? void 0 : _46.length) && (_jsx(_Fragment, { children: _jsxs("div", { className: "my-4 py-4 font-black block border-wfGray-300 border-t border-b", children: ["Total additional cost: ", formatTotal()] }) })), data.fulfillment.benefit.products.length > 0 ? (_jsx("h3", { className: "mt-4 mb-2 block pt-4", children: _jsxs("strong", { children: [data.fulfillment.benefit.name, " Preferences"] }) })) : null, data.fulfillment.benefit.products.filter((product) => product.variants.length).map((product) => {
                return (_jsx(ChooseProductSelection, { product: product, productSelection: data.fulfillment.productSelections.find((productSelection) => productSelection.product.id == product.id), fulfillmentId: id }, product.id));
            }), currentShopifyVersion == 2 && !currentBusinessHasTooManyProducts && ((_47 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _47 === void 0 ? void 0 : _47.curated) && ((_49 = (_48 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _48 === void 0 ? void 0 : _48.benefit) === null || _49 === void 0 ? void 0 : _49.__typename) == "SubscriptionBenefit" && ((_52 = (_51 = (_50 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _50 === void 0 ? void 0 : _50.benefit) === null || _51 === void 0 ? void 0 : _51.business) === null || _52 === void 0 ? void 0 : _52.enableProductSwapping) &&
                _jsxs(_Fragment, { children: [_jsx("h4", { className: "my-4 block pt-4", children: "Customize your\u00A0subscription box" }), _jsxs("p", { className: "my-2", children: [isAddonOnly ? "Add products to your box.  " : "Adjust the products in your box.  ", "Some changes may affect the final price."] }), _jsx(Select, { windowThreshold: 100, ref: selectRef, minMenuHeight: 480, id: `platformId-${id}`, formatOptionLabel: ({ label }) => label, components: { Input, DropdownIndicator, Placeholder }, defaultValue: defaultOptions, isMulti: true, delimiter: ",", onChange: updateFulfillmentWithSelectValue, options: options, filterOption: customFilterSelectOption, placeholder: null, isClearable: false, styles: {
                                multiValue: (provided) => (Object.assign(Object.assign({}, provided), { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: "5px", paddingRight: "5px", backgroundColor: "#f2f2f2" })),
                                multiValueLabel: (provided) => (Object.assign(Object.assign({}, provided), { fontSize: '14px', color: '#333', whiteSpace: 'normal', padding: '10px 12px' })),
                                multiValueRemove: (provided) => (Object.assign(Object.assign({}, provided), { cursor: 'pointer', ':hover': {
                                        color: "#ff877b",
                                    } })),
                                control: (provided) => (Object.assign(Object.assign({}, provided), { borderWidth: "1px !important", borderColor: '#ccc !important', boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)' // added a subtle shadow for depth
                                 })),
                                dropdownIndicator: (provided) => (Object.assign(Object.assign({}, provided), { color: '#888' // subtle color for the dropdown arrow
                                 })),
                                placeholder: (provided) => (Object.assign(Object.assign({}, provided), { color: '#aaa' // lighter color for the placeholder
                                 })),
                                clearIndicator: (provided) => (Object.assign(Object.assign({}, provided), { cursor: 'pointer', fontSize: '24px', color: '#f44336', ':hover': {
                                        color: '#d32f2f'
                                    } })),
                                option: (provided, state) => (Object.assign(Object.assign({}, provided), { padding: '10px 12px', 
                                    // backgroundColor: state.isFocused ? '#f7f7f7' : provided.backgroundColor,
                                    backgroundColor: state.isSelected ? '#f7f7f7' : (state.isFocused ? '#f7f7f7' : provided.backgroundColor), color: state.isSelected ? '#ff877b' : provided.color, cursor: 'pointer' })),
                            }, theme: (theme) => (Object.assign(Object.assign({}, theme), { borderRadius: 0, colors: Object.assign(Object.assign({}, theme.colors), { primary: "#ff877b", primary25: "#ffdad6", primary50: "#ffdad6", primary75: "#ffdad6", neutral20: '#ccc' // added for the dropdown border color
                                 }) })) }), discountedAddonAmount ?
                            _jsxs("p", { className: "my-2" + (discountedPrice > discountedDefaultCurationValue ? " text-wfRed-600" : ""), children: ["By clicking \"Save,\" you'll confirm your selections and any additional charges of ", formattedPrice(majorToMinorPrice(discountedAddonAmount)), " will be applied on your fulfillment date. ", (retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.percentage) > 0 ? _jsxs(_Fragment, { children: ["This amount reflects your ", retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.percentage, "% member discount on the added items."] }) : null] }) : (hasSelectionChanged() ?
                            _jsx("p", { className: "my-2", children: "By clicking \"Save,\" you'll confirm your selections. There are no additional charges for the products you've added to your subscription box." }) : null), _jsx(Button, { className: "mt-4", variant: ButtonVariant.Outlined, onClick: resetCuration, children: "Reset curation" })] }), (() => {
                var _a, _b, _c;
                const benefit = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.benefit;
                if (!benefit)
                    return null;
                // Determine available options
                const shippingEnabled = !!benefit.shippingEnabled;
                const pickupEnabled = !!benefit.pickupEnabled;
                // If neither is enabled, show nothing
                if (!shippingEnabled && !pickupEnabled)
                    return null;
                // Build options array
                const deliveryOptions = [];
                if (shippingEnabled) {
                    let shippingLabel = "Ship to my address";
                    if (benefit.shippingCost && benefit.shippingCost.amount > 0) {
                        shippingLabel += ` (+${formattedPrice(benefit.shippingCost.amount)} shipping)`;
                    }
                    deliveryOptions.push({ value: "SHIPPING", label: shippingLabel });
                }
                if (pickupEnabled) {
                    deliveryOptions.push({ value: "PICKUP", label: "Pick up in-store" });
                }
                // Determine current value
                // Assume data.fulfillment.deliveryMethod is either "shipping" or "pickup"
                // If not present, default to first available
                const currentDeliveryMethod = ((_b = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _b === void 0 ? void 0 : _b.deliveryMethod) || ((_c = deliveryOptions[0]) === null || _c === void 0 ? void 0 : _c.value);
                // Handler for changing delivery method
                const handleDeliveryMethodChange = (e) => {
                    const newValue = e.target.value;
                    client.cache.modify({
                        id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                        fields: {
                            deliveryMethod() {
                                return newValue;
                            },
                        },
                    });
                };
                // If only one option, show disabled input
                if (deliveryOptions.length === 1) {
                    return (_jsxs("div", { className: "my-4 block", children: [_jsx("label", { className: "my-4 pt-4 block font-normal text-xl leading-relaxed", children: "Delivery Preference" }), _jsx("input", { type: "text", className: "p-2 w-full border border-wfGray-300 bg-wfGray-100 text-lg", value: deliveryOptions[0].label, disabled: true, readOnly: true })] }));
                }
                // Otherwise, show select
                return (_jsxs("div", { className: "my-4 block", children: [_jsx("label", { className: "my-4 pt-4 block font-normal text-xl leading-relaxed", htmlFor: "delivery-method-select", children: "Delivery Preference" }), _jsx("select", { id: "delivery-method-select", className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2 text-lg", value: currentDeliveryMethod, onChange: handleDeliveryMethodChange, children: deliveryOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }));
            })(), (data.fulfillment.benefit.variants || []).length > 0 ? (_jsxs(_Fragment, { children: [_jsx("h4", { className: "my-4 block pt-4", children: "Options" }), _jsxs("select", { onChange: updateVariantSelect, value: (_54 = (_53 = data.fulfillment) === null || _53 === void 0 ? void 0 : _53.variant) === null || _54 === void 0 ? void 0 : _54.id, className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2 text-lg", children: [_jsx("option", { children: "Select a Subscription Option!" }), (_57 = (_56 = (_55 = data.fulfillment) === null || _55 === void 0 ? void 0 : _55.benefit) === null || _56 === void 0 ? void 0 : _56.variants) === null || _57 === void 0 ? void 0 : _57.map((variant) => {
                                return _jsx(VariantOption, Object.assign({}, variant), variant.id);
                            })] })] })) : null, _jsx("div", { className: "my-8 block" }), 
            //@ts-ignore
            ((_58 = data.fulfillment.benefit) === null || _58 === void 0 ? void 0 : _58.deliveredToYour) == "door" && data.fulfillment.deliveryMethod == DeliveryMethod.Shipping && (_jsxs(_Fragment, { children: [_jsxs(ExpandableArea, { title: "Shipping Address", expandedStart: true, children: [_jsx("div", { className: "text-wfRed-600 text-base my-2", children: "* Required" }), _jsx(AddressInput, { fulfillmentId: id, label: "Shipping Name", placeholder: "", fieldName: "shippingName", addressFields: (_59 = data.fulfillment) === null || _59 === void 0 ? void 0 : _59.address }), _jsx(AddressInput, { fulfillmentId: id, label: "Address Line 1", fieldName: "line1", addressFields: (_60 = data.fulfillment) === null || _60 === void 0 ? void 0 : _60.address, required: userIsMember || userIsOrganizer, placeholder: "123 Main St.", highlighted: _.contains(highlightedFields, "line1") }), _jsx(AddressInput, { fulfillmentId: id, label: "Address Line 2", placeholder: "", fieldName: "line2", addressFields: (_61 = data.fulfillment) === null || _61 === void 0 ? void 0 : _61.address }), _jsxs("div", { className: "sm:grid sm:grid-cols-4 sm:gap-4", children: [_jsx("div", { className: "sm:col-span-2", children: _jsx(AddressInput, { fulfillmentId: id, label: "City", fieldName: "city", addressFields: (_62 = data.fulfillment) === null || _62 === void 0 ? void 0 : _62.address, required: userIsMember || userIsOrganizer, placeholder: "New York", highlighted: _.contains(highlightedFields, "city") }) }), _jsx("div", { className: "sm:col-span-2", children: _jsx(AddressInput, { fulfillmentId: id, label: "ZIP", fieldName: "zip", addressFields: (_63 = data.fulfillment) === null || _63 === void 0 ? void 0 : _63.address, required: userIsMember || userIsOrganizer, placeholder: "12345", highlighted: _.contains(highlightedFields, "zip") }) })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "state", children: ["State ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(RegionDropdown, { name: "state", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "state")
                                            ? "border-salmon-600"
                                            : "border-wfGray-300"}`, country: ((_65 = (_64 = data.fulfillment) === null || _64 === void 0 ? void 0 : _64.address) === null || _65 === void 0 ? void 0 : _65.country) || "United States", value: (_67 = (_66 = data.fulfillment) === null || _66 === void 0 ? void 0 : _66.address) === null || _67 === void 0 ? void 0 : _67.state, onChange: updateStateSelect })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "country", children: ["Country ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(CountryDropdown, { name: "country", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "country")
                                            ? "border-salmon-600"
                                            : "border-wfGray-300"}`, value: ((_69 = (_68 = data.fulfillment) === null || _68 === void 0 ? void 0 : _68.address) === null || _69 === void 0 ? void 0 : _69.country) || "United States", onChange: updateCountrySelect })] })] }), _jsxs("div", { children: [_jsx("label", { className: "my-4 block", htmlFor: "phoneNumber", children: "Phone Number" }), _jsx(TextInput, { id: "phoneNumber", value: (_72 = (_71 = (_70 = data.fulfillment) === null || _70 === void 0 ? void 0 : _70.membership) === null || _71 === void 0 ? void 0 : _71.user) === null || _72 === void 0 ? void 0 : _72.phoneNumber, onChange: updatePhoneNumber, className: "p-2 w-full text-lg" })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block", htmlFor: "shippingNotes", children: [shippingNotesLabel, " ", shippingNotesRequired && _jsx("span", { className: "text-wfRed-600", children: "*" })] }), ((_74 = (_73 = data.fulfillment) === null || _73 === void 0 ? void 0 : _73.benefit) === null || _74 === void 0 ? void 0 : _74.shippingNotesInstructions) && (_jsx("p", { className: "my-4", children: (_76 = (_75 = data.fulfillment) === null || _75 === void 0 ? void 0 : _75.benefit) === null || _76 === void 0 ? void 0 : _76.shippingNotesInstructions })), _jsx(TextAreaInput, { id: "shippingNotes", value: data.fulfillment.shippingNotes || "", style: { height: "5rem" }, onChange: updateShippingNotes, className: "p-2 w-full text-lg", allowedCharacters: shippingNotesAllowedCharacters })] })] })), (((_78 = (_77 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _77 === void 0 ? void 0 : _77.benefit) === null || _78 === void 0 ? void 0 : _78.__typename) == "SubscriptionBenefit" && !isAfterPurchase) && (_jsxs("div", { className: "my-4 text-md grid grid-cols-12 gap-2", children: [_jsxs("label", { className: "flex font-sans normal-case col-span-11 cursor-pointer", htmlFor: `${namespace}_user-save-to-future-false`, children: [_jsx("input", { type: "radio", className: "accent-salmon-700 shadow-none height-4 mr-4", name: `${namespace}_user-save-to-future`, id: `${namespace}_user-save-to-future-false`, value: "false", checked: !saveToFutureFulfillments, onChange: (e) => {
                                    saveToFutureFulfillmentsVar(e.target.value == "true");
                                } }), "Update only this box."] }), _jsxs("label", { className: "flex font-sans normal-case col-span-11 cursor-pointer", htmlFor: `${namespace}_user-save-to-future-true`, children: [_jsx("input", { type: "radio", name: `${namespace}_user-save-to-future`, id: `${namespace}_user-save-to-future-true`, className: "accent-salmon-700 shadow-none height-4 mr-4", value: "true", checked: saveToFutureFulfillments, onChange: (e) => {
                                    saveToFutureFulfillmentsVar(e.target.value == "true");
                                } }), "Use these settings for all upcoming boxes", userIsOrganizer && " for this member (except for the fulfillment date)", ((_79 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _79 === void 0 ? void 0 : _79.curated) && ". Note: Customized products will only apply to this box."] })] })), saveMessage, _jsx(Button, { className: "my-4", variant: ButtonVariant.Salmon, disabled: primaryActionLoading, onClick: saveFulfillment, children: (((_81 = (_80 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _80 === void 0 ? void 0 : _80.benefit) === null || _81 === void 0 ? void 0 : _81.claimable) && !((_82 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _82 === void 0 ? void 0 : _82.claimedAt)) ? "Claim shipment" : "Save settings" }), showCancelButton && (_jsx(Button, { className: "my-4", variant: ButtonVariant.Outlined, disabled: primaryActionLoading, onClick: cancelSaveFulfillment, children: "Cancel" }))] }));
};
export default ChooseSubscriptionScreen;
